/**
 * nge: folders in the Seg panel (Ames 2026-10-14: "how could we add a folders
 * or grouping system here? There exists a prototype in Batch Processor but
 * it's separate from this all-important menu"). A folder is a named, foldable
 * section of the list; a cell sits in at most one. Folders are saved in the
 * segmentation layer's state (`ngeFolders`), so a saved view or a shared link
 * opens with the same folders. Only membership is kept here: the order of the
 * cells inside a folder is the list's own order, which drag still changes.
 */
import type {SegmentNotes} from 'neuroglancer/ui/nge_segment_notes';
import {NullarySignal} from 'neuroglancer/util/signal';

export interface SegmentFolder {
  id: string;
  name: string;
  ids: string[];
  collapsed?: boolean;
  /** The folder this one sits in (nesting); none = top level. */
  parent?: string;
  /** The colour last given to the whole folder ('#rrggbb'). */
  color?: string;
  /** True once a colour was picked for the folder's cells: cells that join
   *  the folder later take it too. */
  paint?: boolean;
  /** The tallest its box may be, in px, once dragged by the grip under it
   *  (Nseraf, report 179); none = the panel's own cap. */
  height?: number;
  /** Annotation folders: its annotations are not drawn (annkri, report 225). */
  hidden?: boolean;
}

/** nge: cells picked with Ctrl+click or Shift+click, to move, show, hide,
 *  recolour or remove together (Ames 2026-10-14: "there needs to be a way to
 *  easily select and move batches of segIDs"). */
export const pickedSegments = {
  ids: new Set<string>(),
  /** The last cell clicked, where a Shift+click range starts. */
  anchor: undefined as string | undefined,
  changed: new NullarySignal(),
  set(ids: Iterable<string>) {
    this.ids = new Set(ids);
    this.changed.dispatch();
  },
  toggle(id: string) {
    const next = new Set(this.ids);
    if (next.has(id)) next.delete(id); else next.add(id);
    this.ids = next;
    this.anchor = id;
    this.changed.dispatch();
  },
  clear() {
    if (!this.ids.size) return;
    this.ids = new Set();
    this.changed.dispatch();
  },
};

/** nge: Select mode, for a touch screen with no Ctrl or Shift (Nseraf,
 *  report 146). While it is on, a tap on a row picks or unpicks that cell,
 *  as Ctrl+click does. The Select button in the In view heading turns it on
 *  and off (src/drag_reorder.ts reads it). */
export const touchPick = {
  on: false,
  changed: new NullarySignal(),
  set(on: boolean) {
    if (this.on === on) return;
    this.on = on;
    this.changed.dispatch();
  },
};

export const FOLDERS_JSON_KEY = 'ngeFolders';

export class SegmentFolders {
  value: SegmentFolder[] = [];
  changed = new NullarySignal();
  /** Annotation folders: the folder that annotations placed from now on go
   *  into (annkri, report 225: "chose a folder and all the annotations you
   *  place go into there"). Saved by the layer under its own key. */
  activeFolder: string|undefined = undefined;

  setActive(id: string|undefined) {
    const next = id !== undefined && this.value.some(f => f.id === id) ? id : undefined;
    if (next === this.activeFolder) return;
    this.activeFolder = next;
    this.changed.dispatch();
  }

  /** The Seg panel's folders hold cell IDs; annotation folders
   *  (nge_annotation_folders.ts) hold annotation IDs and keep their own
   *  "color what joins later" default. */
  constructor(private options: {validId?: (id: string) => boolean, paintKey?: string} = {}) {}

  restoreState(json: unknown) {
    const out: SegmentFolder[] = [];
    if (Array.isArray(json)) {
      for (const f of json) {
        if (!f || typeof f !== 'object') continue;
        const name = typeof f.name === 'string' && f.name.trim() ? f.name.trim().slice(0, 60) : 'Folder';
        const validId = this.options.validId ?? ((s: string) => /^\d+$/.test(s));
        const ids = Array.isArray(f.ids) ? f.ids.filter((s: unknown) => typeof s === 'string' && validId(s)) : [];
        out.push({
          id: typeof f.id === 'string' && f.id ? f.id : newFolderId(), name, ids,
          collapsed: f.collapsed === true,
          parent: typeof f.parent === 'string' && f.parent ? f.parent : undefined,
          color: typeof f.color === 'string' && /^#[0-9a-f]{6}$/i.test(f.color) ? f.color : undefined,
          paint: f.paint === true,
          hidden: f.hidden === true ? true : undefined,
          height: typeof f.height === 'number' && Number.isFinite(f.height) ?
              Math.round(Math.max(FOLDER_MIN_HEIGHT, Math.min(FOLDER_MAX_HEIGHT, f.height))) : undefined,
        });
      }
    }
    // A parent that is missing, or a loop, puts the folder at the top level.
    const known = new Set(out.map(f => f.id));
    for (const f of out) if (f.parent !== undefined && !known.has(f.parent)) f.parent = undefined;
    this.value = out;
    for (const f of out) if (f.parent !== undefined && this.isInside(f.parent, f.id)) f.parent = undefined;
    this.changed.dispatch();
  }

  /** Only the cells still in the list are written, so a folder never carries
   *  cells that were removed. Nothing is written when there are no folders. */
  toJSON(inList?: (id: string) => boolean) {
    if (!this.value.length) return undefined;
    return this.value.map(f => ({
      id: f.id,
      name: f.name,
      ids: inList ? f.ids.filter(inList) : f.ids,
      ...(f.collapsed ? {collapsed: true} : {}),
      ...(f.parent ? {parent: f.parent} : {}),
      ...(f.color ? {color: f.color} : {}),
      ...(f.paint ? {paint: true} : {}),
      ...(f.hidden ? {hidden: true} : {}),
      ...(f.height ? {height: f.height} : {}),
    }));
  }

  /** True when folder `id` is `ancestor` or sits somewhere inside it. */
  isInside(id: string, ancestor: string): boolean {
    let cur: string|undefined = id;
    for (let guard = 0; cur !== undefined && guard < 100; ++guard) {
      if (cur === ancestor) return true;
      cur = this.value.find(f => f.id === cur)?.parent;
    }
    return false;
  }

  /** The folder and every folder inside it. */
  subtree(id: string): string[] {
    const out = [id];
    for (let i = 0; i < out.length; ++i) {
      for (const f of this.value) if (f.parent === out[i]) out.push(f.id);
    }
    return out;
  }

  /** The cells in a folder and in every folder inside it. */
  cellsIn(id: string): string[] {
    const ids = new Set(this.subtree(id));
    return this.value.filter(f => ids.has(f.id)).flatMap(f => f.ids);
  }

  /** Folders in display order, each under its parent, with its depth. */
  ordered(): {folder: SegmentFolder, depth: number, hidden: boolean}[] {
    const out: {folder: SegmentFolder, depth: number, hidden: boolean}[] = [];
    const walk = (parent: string|undefined, depth: number, hidden: boolean) => {
      for (const f of this.value) {
        if (f.parent !== parent) continue;
        out.push({folder: f, depth, hidden});
        walk(f.id, depth + 1, hidden || !!f.collapsed);
      }
    };
    walk(undefined, 0, false);
    return out;
  }

  /** Put a folder inside another (undefined = top level); never inside itself. */
  setParent(id: string, parent: string|undefined) {
    if (parent !== undefined && this.isInside(parent, id)) return;
    this.value = this.value.map(f => f.id === id ? {...f, parent} : f);
    this.changed.dispatch();
  }

  /** Put a folder just before or after another, beside it (same parent);
   *  never next to something inside itself (Ames 2026-10-15: "can I drag to
   *  reorder folders"). */
  placeFolder(id: string, targetId: string, where: 'before'|'after') {
    if (id === targetId || this.isInside(targetId, id)) return;
    const target = this.value.find(f => f.id === targetId);
    const moving = this.value.find(f => f.id === id);
    if (!target || !moving) return;
    const rest = this.value.filter(f => f.id !== id);
    const at = rest.indexOf(target) + (where === 'after' ? 1 : 0);
    // after a folder means after everything inside it too
    let end = at;
    if (where === 'after') {
      const inside = new Set(this.subtree(targetId));
      while (end < rest.length && inside.has(rest[end].id)) ++end;
    }
    rest.splice(where === 'after' ? end : at, 0, {...moving, parent: target.parent});
    this.value = rest;
    this.changed.dispatch();
  }

  /** The box's height from the grip; undefined goes back to the panel's cap. */
  setHeight(id: string, height: number|undefined) {
    const h = height === undefined ? undefined :
        Math.round(Math.max(FOLDER_MIN_HEIGHT, Math.min(FOLDER_MAX_HEIGHT, height)));
    this.value = this.value.map(f => f.id === id ? {...f, height: h} : f);
    this.changed.dispatch();
  }

  setHidden(id: string, hidden: boolean) {
    this.value = this.value.map(f => f.id === id ? {...f, hidden: hidden || undefined} : f);
    this.changed.dispatch();
  }

  /** True when the folder or one it sits inside is hidden. */
  isHidden(id: string|undefined): boolean {
    for (let cur = id, guard = 0; cur !== undefined && guard < 100; ++guard) {
      const f = this.value.find(x => x.id === cur);
      if (f === undefined) return false;
      if (f.hidden) return true;
      cur = f.parent;
    }
    return false;
  }

  setColor(id: string, color: string) {
    this.value = this.value.map(f => f.id === id ? {...f, color} : f);
    this.changed.dispatch();
  }

  /** Whether cells that join the folder later take its colour (off unless
   *  turned on in the folder's colour palette; Ames 2026-10-14). */
  setPaint(id: string, paint: boolean) {
    // the choice is kept: new folders start with it (Ames 2026-10-14)
    try { localStorage.setItem(this.options.paintKey ?? PAINT_DEFAULT_KEY, paint ? '1' : '0'); } catch { /* private window */ }
    this.value = this.value.map(f => f.id === id ? {...f, paint} : f);
    this.changed.dispatch();
  }

  folderOf(segId: string): SegmentFolder|undefined {
    return this.value.find(f => f.ids.includes(segId));
  }

  memberIds(): Set<string> {
    const s = new Set<string>();
    for (const f of this.value) for (const id of f.ids) s.add(id);
    return s;
  }

  create(name = 'New folder', parent?: string): SegmentFolder {
    // Each new folder gets its own colour (Ames 2026-10-14), the first of
    // these not yet used, for its dot and its rail. The cells keep theirs
    // until a colour is picked on the dot.
    const used = new Set(this.value.map(f => f.color));
    const color = FOLDER_COLORS.find(c => !used.has(c)) ??
        FOLDER_COLORS[this.value.length % FOLDER_COLORS.length];
    const f: SegmentFolder = {id: newFolderId(), name, ids: [], parent, color, paint: paintDefault(this.options.paintKey)};
    this.value = [...this.value, f];
    this.changed.dispatch();
    return f;
  }

  rename(id: string, name: string) {
    const clean = name.trim().slice(0, 60);
    if (!clean) return;
    this.value = this.value.map(f => f.id === id ? {...f, name: clean} : f);
    this.changed.dispatch();
  }

  setCollapsed(id: string, collapsed: boolean) {
    this.value = this.value.map(f => f.id === id ? {...f, collapsed} : f);
    this.changed.dispatch();
  }

  /** Ungroup: the folder goes, its cells go back to the main list and the
   *  folders inside it move up a level. */
  remove(id: string) {
    if (this.activeFolder === id) this.activeFolder = undefined;
    const gone = this.value.find(f => f.id === id);
    this.value = this.value.filter(f => f.id !== id)
                     .map(f => f.parent === id ? {...f, parent: gone?.parent} : f);
    this.changed.dispatch();
  }

  /** Put cells in a folder ('' = no folder), taking them out of any other. */
  move(segIds: string[], folderId: string) {
    this.value = this.value.map(f => {
      const kept = f.ids.filter(id => !segIds.includes(id));
      return f.id === folderId ? {...f, ids: [...kept, ...segIds]} : {...f, ids: kept};
    });
    this.changed.dispatch();
  }
}

const PAINT_DEFAULT_KEY = 'nge-folder-paint-default';
export const FOLDER_MIN_HEIGHT = 48;
export const FOLDER_MAX_HEIGHT = 2000;
function paintDefault(key = PAINT_DEFAULT_KEY): boolean {
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
}

export const FOLDER_COLORS = ['#4a9eff', '#7cffb2', '#f5d142', '#c084fc', '#fb923c', '#2dd4bf', '#f472b6', '#ff2d2d'];

/**
 * When a cell's ID is replaced (an out of date ID updated to its current
 * pieces, a merge, a split), the new IDs take the old one's place: in its
 * folder (Ames 2026-10-14: "shouldn't those pieces stay in the folder they
 * were in?"). Every path that replaces an ID removes the old and adds the new
 * in one go, so a removal and an addition in the same moment are a
 * replacement; a cell added later on its own is not.
 */
export function followReplacements(
    selectedSegments: {changed: {add(fn: (x: any, add: boolean) => void): () => void}},
    folders: SegmentFolders, pasted?: () => {ids: string[], changed: NullarySignal},
    notes?: SegmentNotes) {
  let removed: string[] = [];
  let added: string[] = [];
  let pending = false;
  const flush = () => {
    pending = false;
    const gone = removed, came = added;
    removed = [];
    added = [];
    if (!gone.length || !came.length) return;
    notes?.replace(gone.filter(id => !came.includes(id)), came.filter(id => !gone.includes(id)));
    const fresh = came.filter(id => !gone.includes(id) && !folders.folderOf(id));
    if (!fresh.length) return;
    const home = gone.map(id => folders.folderOf(id)).find(f => f !== undefined);
    if (home !== undefined) folders.move(fresh, home.id);
    const p = pasted?.();
    if (p && gone.some(id => p.ids.includes(id))) {
      const at = Math.min(...gone.map(id => p.ids.indexOf(id)).filter(i => i >= 0));
      p.ids = [...p.ids.slice(0, at), ...fresh.filter(id => !p.ids.includes(id)), ...p.ids.slice(at)];
      p.changed.dispatch();
    }
  };
  return selectedSegments.changed.add((x: any, add: boolean) => {
    if (x == null) return;
    const ids = (Array.isArray(x) ? x : [x]).map((u: any) => u.toString());
    (add ? added : removed).push(...ids);
    if (!pending) {
      pending = true;
      Promise.resolve().then(flush);
    }
  });
}

/**
 * A small palette under a colour dot (Ames 2026-10-14: "currently I have to
 * move the color to make it recolor them"; the browser's picker only acts
 * when the colour changes). One click on a colour, the current one too,
 * applies it; Custom opens the full picker, which applies as it moves.
 */
const SAVED_COLORS_KEY = 'nge-custom-colors';
function savedColors(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(SAVED_COLORS_KEY) || '[]');
    return Array.isArray(v) ? v.filter((c: unknown) => typeof c === 'string' && /^#[0-9a-f]{6}$/i.test(c)).slice(0, 8) : [];
  } catch {
    return [];
  }
}
function saveColor(hex: string) {
  try {
    const c = hex.toLowerCase();
    const next = [c, ...savedColors().filter(x => x.toLowerCase() !== c)].slice(0, 8);
    localStorage.setItem(SAVED_COLORS_KEY, JSON.stringify(next));
  } catch { /* private window: not kept */ }
}

export function openColorPalette(
    anchor: HTMLElement, current: string|undefined, onPick: (hex: string) => void,
    title = 'Color', toggle?: {label: string, value: boolean, onChange: (on: boolean) => void}) {
  document.querySelector('.nge-color-palette')?.remove();
  const pop = document.createElement('div');
  pop.className = 'nge-color-palette';
  const head = document.createElement('div');
  head.className = 'nge-color-palette-title';
  head.textContent = title;
  pop.appendChild(head);
  const grid = document.createElement('div');
  grid.className = 'nge-color-palette-grid';
  const close = () => {
    pop.remove();
    document.removeEventListener('mousedown', outside, true);
    document.removeEventListener('keydown', esc, true);
  };
  for (const c of FOLDER_COLORS) {
    const b = document.createElement('button');
    b.className = 'nge-color-palette-chip' + (current && c.toLowerCase() === current.toLowerCase() ? ' nge-current' : '');
    b.style.background = c;
    b.title = c;
    b.addEventListener('click', () => { onPick(c); close(); });
    grid.appendChild(b);
  }
  pop.appendChild(grid);
  // Colours picked with Custom are kept (the last 8, in this browser) and
  // offered again here (Ames 2026-10-14).
  const mine = savedColors().filter(c => !FOLDER_COLORS.includes(c));
  if (mine.length) {
    const label = document.createElement('div');
    label.className = 'nge-color-palette-title nge-color-palette-mine';
    label.textContent = 'Your colors';
    const row = document.createElement('div');
    row.className = 'nge-color-palette-grid';
    for (const c of mine) {
      const b = document.createElement('button');
      b.className = 'nge-color-palette-chip' + (current && c.toLowerCase() === current.toLowerCase() ? ' nge-current' : '');
      b.style.background = c;
      b.title = c;
      b.addEventListener('click', () => { onPick(c); close(); });
      row.appendChild(b);
    }
    pop.append(label, row);
  }
  const custom = document.createElement('label');
  custom.className = 'nge-color-palette-custom';
  const input = document.createElement('input');
  input.type = 'color';
  input.value = current && /^#[0-9a-f]{6}$/i.test(current) ? current : '#8cb4eb';
  input.addEventListener('input', () => onPick(input.value));
  input.addEventListener('change', () => { onPick(input.value); saveColor(input.value); close(); });
  custom.append(input, document.createTextNode(' Custom\u2026'));
  pop.appendChild(custom);
  if (toggle !== undefined) {
    const row = document.createElement('label');
    row.className = 'nge-color-palette-custom nge-color-palette-toggle';
    const box = document.createElement('input');
    box.type = 'checkbox';
    box.checked = toggle.value;
    box.addEventListener('change', () => toggle.onChange(box.checked));
    row.append(box, document.createTextNode(' ' + toggle.label));
    pop.appendChild(row);
  }
  document.body.appendChild(pop);
  const r = anchor.getBoundingClientRect();
  const w = pop.offsetWidth, h = pop.offsetHeight;
  pop.style.left = `${Math.max(8, Math.min(window.innerWidth - w - 8, r.left))}px`;
  pop.style.top = `${r.bottom + 6 + h > window.innerHeight ? Math.max(8, r.top - h - 6) : r.bottom + 6}px`;
  const outside = (e: MouseEvent) => {
    if (!pop.contains(e.target as Node) && e.target !== anchor) close();
  };
  const esc = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.stopPropagation(); close(); }
  };
  setTimeout(() => {
    document.addEventListener('mousedown', outside, true);
    document.addEventListener('keydown', esc, true);
  }, 0);
}

function newFolderId() {
  return 'f' + Math.random().toString(36).slice(2, 9);
}
